import { useEffect, useState } from 'react';
import { adminApi } from '../../utils/api';
import { Alert, Button, Modal, Select, TagsInput, Toggle } from './shared';
import CredentialFormModal, { EMPTY_CREDENTIAL } from './CredentialFormModal';

export const EMPTY_SYNC_CONFIG = {
    id: '',
    name: '',
    description: '',
    provider: 'git',
    resource_type: 'skill',
    credential_id: '',
    paths: [],
    branch: '',
    tags: [],
    target_environment: '',
    is_active: true,
};

const RESOURCE_TYPE_OPTIONS = [
    { value: 'skill', label: 'Skill — /skills/{name}/SKILL.md' },
    { value: 'subagent', label: 'Sub-agent — /subagents/{name}/SUBAGENT.md' },
    { value: 'knowledge', label: 'Knowledge — /knowledge/{name}/KNOWLEDGE.md' },
];

const sanitizeTag = (s) => (s || '').trim().toLowerCase().replace(/[^a-z0-9_-]/g, '-').replace(/^-+|-+$/g, '');

export default function SyncConfigFormModal({ open, onClose, initial, isExisting, onSave, defaultResourceType }) {
    const [form, setForm] = useState(EMPTY_SYNC_CONFIG);
    const [credentials, setCredentials] = useState([]);
    const [environments, setEnvironments] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState('');
    const [showCredentialForm, setShowCredentialForm] = useState(false);
    const [autoTag, setAutoTag] = useState('');

    useEffect(() => {
        if (!open) return;
        adminApi.listCredentials({ provider: 'git' }).then(setCredentials).catch(() => setCredentials([]));
        adminApi.listEnvironments().then(setEnvironments).catch(() => setEnvironments([]));
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const merged = { ...EMPTY_SYNC_CONFIG, ...(initial || {}) };
        if (defaultResourceType && !isExisting) merged.resource_type = defaultResourceType;
        const branchTag = sanitizeTag(merged.branch || '');
        if (!isExisting && branchTag && (!merged.tags || merged.tags.length === 0)) {
            merged.tags = [branchTag];
            setAutoTag(branchTag);
        } else {
            setAutoTag('');
        }
        setForm(merged);
        setErr('');
    }, [open, initial, isExisting, defaultResourceType]);

    const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

    const handleBranchChange = (e) => {
        const nextBranch = e.target.value;
        const nextTag = sanitizeTag(nextBranch);
        setForm((prev) => {
            const tags = prev.tags || [];
            const isAuto = autoTag && tags.length === 1 && tags[0] === autoTag;
            const isEmpty = tags.length === 0;
            if (nextTag && (isAuto || isEmpty)) {
                setAutoTag(nextTag);
                return { ...prev, branch: nextBranch, tags: [nextTag] };
            }
            return { ...prev, branch: nextBranch };
        });
    };

    const handleCredentialCreated = async (payload) => {
        const created = await adminApi.createCredential(payload);
        setShowCredentialForm(false);
        const list = await adminApi.listCredentials({ provider: 'git' });
        setCredentials(list);
        if (created?.id) set('credential_id', created.id);
    };

    const submit = async () => {
        setErr('');
        if (!form.id || !form.name) return setErr('ID and Name are required');
        if (!form.credential_id) return setErr('Pick a credential (or create a new one)');
        if (!form.resource_type) return setErr('Resource type is required');
        if (!form.target_environment) return setErr('Target environment is required');

        setSubmitting(true);
        try {
            await onSave({
                ...form,
                paths: form.paths || [],
                tags: form.tags || [],
                description: form.description || null,
                branch: form.branch || null,
            });
        } catch (e) {
            setErr(e?.message || String(e));
        } finally {
            setSubmitting(false);
        }
    };

    const credOptions = [
        { value: '', label: '— select credential —' },
        ...credentials.map((c) => ({
            value: c.id,
            label: `${c.name} (${c.id}) — ${c.provider_data?.repo_url || c.provider}`,
        })),
    ];

    return (
        <>
            <Modal open={open} onClose={() => !submitting && onClose()} size="lg" title={isExisting ? `Edit "${form.id}"` : 'Add sync config'}>
                <Modal.Body>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">ID *</label>
                                <input
                                    type="text"
                                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
                                    placeholder="dev-skills"
                                    value={form.id}
                                    disabled={isExisting}
                                    onChange={(e) => set('id', e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '-'))}
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-700 mb-1">Name *</label>
                                <input
                                    type="text"
                                    className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    placeholder="Dev skills sync"
                                    value={form.name}
                                    onChange={(e) => set('name', e.target.value)}
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Description <span className="text-gray-400 font-normal">(optional)</span>
                            </label>
                            <input
                                type="text"
                                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                value={form.description || ''}
                                onChange={(e) => set('description', e.target.value)}
                            />
                        </div>

                        <Select
                            label="Resource type *"
                            value={form.resource_type}
                            options={RESOURCE_TYPE_OPTIONS}
                            onChange={(e) => set('resource_type', e.target.value)}
                            disabled={isExisting}
                        />

                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <label className="block text-xs font-medium text-gray-700">Credential *</label>
                                <button type="button" className="text-xs text-blue-500 hover:underline" onClick={() => setShowCredentialForm(true)}>
                                    + New credential
                                </button>
                            </div>
                            <select
                                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
                                value={form.credential_id || ''}
                                onChange={(e) => set('credential_id', e.target.value)}
                            >
                                {credOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Branch <span className="text-gray-400 font-normal">(optional — falls back to credential's default)</span>
                            </label>
                            <input
                                type="text"
                                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                                placeholder="main"
                                value={form.branch || ''}
                                onChange={handleBranchChange}
                            />
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                Scan Paths <span className="text-gray-400 font-normal">(optional — leave empty for repo root)</span>
                            </label>
                            <PathsInput paths={form.paths || []} onChange={(paths) => set('paths', paths)} disabled={submitting} />
                        </div>

                        <TagsInput
                            label="Tags"
                            value={form.tags || []}
                            onChange={(tags) => {
                                if (autoTag && (tags.length !== 1 || tags[0] !== autoTag)) setAutoTag('');
                                set('tags', tags);
                            }}
                            helpText="Defaults to the branch name. Applied to every imported resource for include_tags filtering."
                        />

                        <Select
                            label="Target environment *"
                            value={form.target_environment || ''}
                            options={[{ value: '', label: '— select env —' }, ...environments.map(e => ({ value: e.id, label: `${e.id} ${e.is_default ? '(default)' : ''}` }))]}
                            onChange={(e) => set('target_environment', e.target.value)}
                        />

                        <Toggle label="Active" checked={form.is_active} onChange={(v) => set('is_active', v)} />

                        {err && <Alert variant="danger">{err}</Alert>}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
                    <Button onClick={submit} loading={submitting}>{isExisting ? 'Save' : 'Add config'}</Button>
                </Modal.Footer>
            </Modal>

            <CredentialFormModal
                open={showCredentialForm}
                onClose={() => setShowCredentialForm(false)}
                initial={EMPTY_CREDENTIAL}
                isExisting={false}
                onSave={handleCredentialCreated}
            />
        </>
    );
}

function PathsInput({ paths, onChange, disabled }) {
    const [inputValue, setInputValue] = useState('');

    const addPath = () => {
        const trimmed = inputValue.trim().replace(/^\/+|\/+$/g, '');
        if (!trimmed || paths.includes(trimmed)) {
            setInputValue('');
            return;
        }
        onChange([...paths, trimmed]);
        setInputValue('');
    };

    const removePath = (i) => onChange(paths.filter((_, idx) => idx !== i));

    return (
        <div>
            <div className="min-h-[38px] w-full text-sm border border-gray-200 rounded-lg px-2 py-1.5 flex flex-wrap gap-1.5 items-center focus-within:ring-2 focus-within:ring-blue-500">
                {paths.map((p, i) => (
                    <span key={i} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 border border-blue-200 rounded px-1.5 py-0.5 text-xs font-mono shrink-0">
                        {p}
                        {!disabled && (
                            <button type="button" className="text-blue-400 hover:text-blue-700 leading-none" onClick={() => removePath(i)}>×</button>
                        )}
                    </span>
                ))}
                <input
                    type="text"
                    className="flex-1 min-w-[120px] outline-none bg-transparent text-sm font-mono placeholder-gray-400"
                    placeholder={paths.length === 0 ? 'skills/tier1' : 'Add path...'}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') { e.preventDefault(); addPath(); }
                        else if (e.key === 'Backspace' && !inputValue && paths.length > 0) onChange(paths.slice(0, -1));
                    }}
                    onBlur={addPath}
                    disabled={disabled}
                />
            </div>
            <p className="text-xs text-gray-400 mt-1">Each path scanned 1 level deep for the resource manifest file.</p>
        </div>
    );
}
